import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, X, ArrowRight, ArrowLeft, AlertCircle, Target, Users, ShieldCheck, Plus } from "lucide-react";
import { Container, PrimaryLink } from "@/components/ui-kit";
import { getPlanBySlug, getRegionalPlan, PLANS, PLAN_ADD_ONS, getPlanPrice } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";

export default function PlanDetail() {
  const { slug } = useParams();
  const { region, isPH, link } = useRegion();
  const basePlan = getPlanBySlug(slug);

  if (!basePlan) {
    return <Navigate to={link("/plans")} replace />;
  }

  const plan = getRegionalPlan(basePlan, region);

  const { price, period } = getPlanPrice(plan.id, region);
  const startUrl = link(`/checkout?plan=${plan.slug}&market=${region}`);
  const others = PLANS.filter((p) => p.slug !== plan.slug);
  const addOns = (basePlan.addOns || [])
    .map((addOn) => PLAN_ADD_ONS.find((full) => full.id === addOn.id))
    .filter(Boolean);

  return (
    <div data-testid={`plan-detail-${plan.slug}`}>
      {/* HERO */}
      <section className="bg-[#0F172A]">
        <Container className="py-14 md:py-20">
          <Link
            to={link("/plans")}
            data-testid="plan-detail-back"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#10B981] transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Back to all plans
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {plan.highlight && (
                <span className="inline-block rounded-full bg-[#10B981] px-3 py-1 text-xs font-semibold text-white mb-4">
                  ⭐ {plan.badge}
                </span>
              )}
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-2">
                {plan.type}
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-display text-white text-balance">{plan.name}</h1>
              <p className="mt-4 text-lg text-slate-300 leading-relaxed">{plan.subtitle}</p>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-4xl font-bold font-display text-white">{price}</span>
                <span className="text-slate-300">{period}</span>
                <span className="text-sm text-slate-400 ml-1">({plan.note})</span>
              </div>
              {isPH && (
                <p className="mt-3 text-sm text-slate-400 max-w-md">
                  Standard Philippine price for the described scope. Custom requirements are quoted before additional work begins.
                </p>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <PrimaryLink to={startUrl} testId="plan-detail-start-top">
                  Start This Plan
                </PrimaryLink>
                <Link
                  to={link("/contact")}
                  data-testid="plan-detail-talk"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/5 transition-colors"
                >
                  Ask a question
                </Link>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden bg-white border border-white/10 shadow-2xl">
              <div className="aspect-[3/4] max-w-sm mx-auto w-full p-2">
                <img src={plan.image} alt={plan.name} className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* BODY */}
      <section className="py-16 md:py-20 bg-white">
        <Container className="max-w-4xl">
          {/* What this plan is for */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="rounded-xl bg-[#10B981]/10 p-2.5"><Target size={20} className="text-[#10B981]" /></span>
              <h2 className="text-2xl font-bold font-display text-[#0F172A]">What this plan is for</h2>
            </div>
            <p className="text-[#475569] leading-relaxed text-lg">{plan.purpose}</p>
          </div>

          {plan.extraSections?.map((section) => (
            <div key={section.title} className="rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] p-7 mb-12">
              <h2 className="text-xl font-bold font-display text-[#0F172A]">{section.title}</h2>
              <p className="mt-3 text-[#475569] leading-relaxed">{section.text}</p>
            </div>
          ))}

          {/* Included / Not included */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold font-display text-[#0F172A] mb-4">What is included</h3>
              <div className="space-y-3">
                {plan.included.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="rounded-full bg-[#10B981]/10 p-1 mt-0.5"><Check size={14} className="text-[#10B981]" /></span>
                    <span className="text-sm text-[#1E293B]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-7">
              <h3 className="text-lg font-semibold font-display text-[#0F172A] mb-4">What is not included</h3>
              <div className="space-y-3">
                {plan.excluded.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="rounded-full bg-slate-200 p-1 mt-0.5"><X size={14} className="text-[#64748B]" /></span>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Optional add-ons (e.g. Assisted Setup & Launch on Website Ownership) */}
          {addOns.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="rounded-xl bg-[#10B981]/10 p-2.5"><Plus size={20} className="text-[#10B981]" /></span>
                <h2 className="text-2xl font-bold font-display text-[#0F172A]">Optional add-on</h2>
              </div>
              {addOns.map((addOn) => {
                const regionalAddOn = getRegionalPlan(addOn, region);
                const addOnPrice = getPlanPrice(addOn.id, region);
                return (
                  <div
                    key={addOn.id}
                    data-testid={`plan-detail-addon-${addOn.id}`}
                    className="rounded-2xl border-2 border-dashed border-[#10B981]/40 bg-[#10B981]/5 p-7"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h3 className="text-lg font-bold font-display text-[#0F172A]">{regionalAddOn.name}</h3>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-bold font-display text-[#0F172A]">+{addOnPrice.price}</span>
                        <span className="text-sm text-[#475569]">{addOnPrice.period}</span>
                      </div>
                    </div>
                    <p className="mt-2 text-[#475569] leading-relaxed">{regionalAddOn.summary}</p>
                    <div className="mt-4 space-y-2.5">
                      {regionalAddOn.included.map((item) => (
                        <div key={item} className="flex items-start gap-2.5">
                          <Check size={16} className="text-[#10B981] mt-0.5 shrink-0" />
                          <span className="text-sm text-[#1E293B]">{item}</span>
                        </div>
                      ))}
                    </div>
                    {regionalAddOn.footnote && (
                      <p className="mt-4 text-xs text-[#475569]">{regionalAddOn.footnote}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Best for */}
          <div className="rounded-2xl bg-[#0F172A] p-8 mb-12">
            <div className="flex items-center gap-3 mb-3">
              <span className="rounded-xl bg-white/10 p-2.5"><Users size={20} className="text-[#34D399]" /></span>
              <h2 className="text-2xl font-bold font-display text-white">Best for</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-lg">{plan.best}</p>
            <p className="mt-4 text-[#34D399] font-medium italic">{plan.positioning}</p>
          </div>

          {/* Important notes */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="rounded-xl bg-amber-50 p-2.5"><AlertCircle size={20} className="text-amber-500" /></span>
              <h2 className="text-2xl font-bold font-display text-[#0F172A]">Important notes</h2>
            </div>
            <div className="space-y-3">
              {plan.notes.map((n) => (
                <div key={n} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#10B981] shrink-0" />
                  <span className="text-[#475569] leading-relaxed">{n}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Honesty note */}
          <div className="rounded-2xl border-l-4 border-[#10B981] bg-[#F8FAFC] p-6">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-[#10B981] mt-0.5 shrink-0" />
              <p className="text-[#1E293B] leading-relaxed">
                We do not promise traffic, leads, SEO rankings, sales, or business results. We provide website
                responsibility, maintenance, monitoring, updates, and technical continuity depending on the selected
                plan.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F8FAFC]">
        <Container>
          <div className="rounded-3xl bg-white border border-[#E2E8F0] shadow-sm p-10 md:p-14 text-center">
            <h2 className="text-3xl font-bold font-display text-[#0F172A] text-balance">Ready to start the {plan.name}?</h2>
            <p className="mt-4 text-lg text-[#475569] max-w-2xl mx-auto">
              We'll confirm what you have, what you need, and get you set up. No pressure, no sales scripts.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryLink to={startUrl} testId="plan-detail-start-bottom">
                Start This Plan
              </PrimaryLink>
              <Link
                to={link("/plans")}
                data-testid="plan-detail-compare"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#E2E8F0] bg-white px-7 py-3.5 text-sm font-semibold text-[#0F172A] hover:border-[#0F172A] transition-colors"
              >
                Compare all plans
              </Link>
            </div>
          </div>

          {/* Other plans */}
          <div className="mt-16">
            <h3 className="text-center text-sm font-semibold uppercase tracking-[0.18em] text-[#10B981] mb-8">
              Other plans
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {others.map((p) => {
                const opr = getPlanPrice(p.id, region);
                return (
                <Link
                  key={p.slug}
                  to={link(`/plans/${p.slug}`)}
                  data-testid={`plan-detail-other-${p.id}`}
                  className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">{p.type}</span>
                  <h4 className="mt-1 font-bold font-display text-[#0F172A] group-hover:text-[#10B981] transition-colors">{p.name}</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-xl font-bold font-display text-[#0F172A]">{opr.price}</span>
                    <span className="text-xs text-[#475569]">{opr.period}</span>
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#10B981]">
                    View details <ArrowRight size={14} />
                  </span>
                </Link>
                );
              })}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
